import React, { useEffect, useState } from 'react'
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  CircularProgress
} from '@mui/material'
import { hashHistory } from 'react-router'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import TablePaginationInc from '../../../../../shared-ui/TablePaginationInc'
import AlertDialog from '../../../../../components/AlertDialog/index.es6'
import { formatDateLocale } from '../../../../../lib/utils.es6'
import useProductCurrency from '../../../../../hooks/useProductCurrency'
import {
  getQuotationsByStatus,
  confirmQuotation,
  rejectQuotation
} from '../../../../../lib/requests.es6'
import QuotationDecision from '../../../PoliciesProcessing/Components/QuotationDecision/QuotationDecision'
import QuotationChanges from './QuotationChanges'
import { getPartner } from '../../../../../lib/access.es6'
import { getFullName } from '../../../../../lib/personDetails.es6'

// An amendment creates a new quotation that sits in Un_Confirmed until it is
// approved (confirmed) or rejected. This dashboard surfaces those quotations.
const TABS = [
  { label: 'Awaiting Review', status: 'Unconfirmed' },
  { label: 'Rejected', status: 'Rejected' }
]

const getPrincipalName = quote => {
  const entries = quote.policy_schedule_entries || []
  const principal = entries.find(
    entry => entry.dependant && entry.dependant.relationship === 'Principal'
  )
  if (principal && principal.dependant) {
    const name = getFullName(principal.dependant)
    if (name) return name
  }
  const fromAd = getFullName(quote.additional_details)
  if (fromAd) return fromAd
  return 'N/A'
}

const QuoteCard = ({ quote, showDecision, onDecision }) => {
  const currencyCode = useProductCurrency(quote.product_code)
  const ad = quote.additional_details || {}
  const policyNumber = ad.policy_number || null
  const premium =
    typeof quote.total_premium_amount_in_cents === 'number'
      ? `${currencyCode} ${(
          quote.total_premium_amount_in_cents / 100
        ).toLocaleString()}`
      : 'N/A'

  return (
    <Card sx={{ mb: 2, boxShadow: 0, border: '1px solid #e2e8f0' }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }}
        >
          <Box>
            {/* The quotation number is an internal reference and was asked to
                be dropped from this list — the policy number identifies the
                amendment for the reviewer. */}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {policyNumber || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#718096' }}>
              <strong>Principal:</strong> {getPrincipalName(quote)}
            </Typography>
          </Box>
          <Chip
            label={quote.status || 'N/A'}
            color={quote.status === 'Rejected' ? 'error' : 'warning'}
            size="small"
            variant="outlined"
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" sx={{ color: '#718096' }}>
              Product
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {quote.product_name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" sx={{ color: '#718096' }}>
              Premium
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {premium}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" sx={{ color: '#718096' }}>
              Created
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatDateLocale(quote.created_at) || 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1
          }}
        >
          {showDecision && (
            <QuotationDecision quote={quote} onDecision={onDecision} />
          )}
          {policyNumber && (
            <ButtonInc
              variant="contained"
              onClick={() =>
                hashHistory.push(`admin/policy-processing/${policyNumber}`)
              }
            >
              View Policy
            </ButtonInc>
          )}
        </Box>

        <QuotationChanges quote={quote} currencyCode={currencyCode} />
      </CardContent>
    </Card>
  )
}

const readError = error => {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (error.message) return error.message
  return 'An unexpected error occurred'
}

var DEFAULT_SIZE = "10"

const DisplayAmendedPolicies = () => {
  var [tab, setTab] = useState(0)
  var [quotes, setQuotes] = useState([])
  var [total, setTotal] = useState(0)
  var [loading, setLoading] = useState(false)
  var [decisionLoader, setDecisionLoader] = useState(false)
  var [decisionSuccess, setDecisionSuccess] = useState(false)
  var [decisionError, setDecisionError] = useState(null)

  var [awaitingPage, setAwaitingPage] = useState("0")
  var [awaitingSize, setAwaitingSize] = useState(DEFAULT_SIZE)
  var [rejectedPage, setRejectedPage] = useState("0")
  var [rejectedSize, setRejectedSize] = useState(DEFAULT_SIZE)

  var partner = getPartner()
  var partnerGuid = partner.guid

  var fetchQuotes = function(page, size) {
    setLoading(true)
    getQuotationsByStatus({
      status: TABS[tab].status,
      partner: partnerGuid,
      page: page + 1,
      size: size
    })
      .then(function(response) {
        var data = response && response.data ? response.data : []
        var list = Array.isArray(data) ? data : data.data || []
        var count = data.total || list.length
        setQuotes(list)
        setTotal(count)
      })
      .catch(function() {
        setQuotes([])
        setTotal(0)
      })
      .finally(function() { setLoading(false) })
  }

  var currentPage = tab === 0 ? awaitingPage : rejectedPage
  var currentSize = tab === 0 ? awaitingSize : rejectedSize

  useEffect(function() {
    fetchQuotes(currentPage, currentSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, awaitingPage, awaitingSize, rejectedPage, rejectedSize])

  var handleTabChange = function(_, v) {
    setTab(v)
    if (v === 0) setAwaitingPage(0)
    else setRejectedPage(0)
  }

  var handlePaginationChange = function(newPage, newSize) {
    if (tab === 0) {
      setAwaitingPage(newPage)
      setAwaitingSize(newSize)
    } else {
      setRejectedPage(newPage)
      setRejectedSize(newSize)
    }
  }

  var onDecision = function(guid, decision) {
    setDecisionLoader(true)
    setDecisionError(null)
    var request = decision === 'reject' ? rejectQuotation : confirmQuotation
    request({ guid })
      .then(function() {
        setDecisionLoader(false)
        setDecisionSuccess(true)
        fetchQuotes(currentPage, currentSize)
      })
      .catch(function(e) {
        setDecisionLoader(false)
        setDecisionError(
          e && e.response ? e.response.data : 'An unexpected error occurred'
        )
      })
  }

  return (
    <Box sx={{ p: 2 }}>
      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{ mb: 2 }}
      >
        {TABS.map(item => (
          <Tab key={item.status} label={item.label} />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : quotes.length === 0 ? (
        <Typography sx={{ textAlign: 'center', py: 6, color: '#6b6b6b' }}>
          No amended policies found
        </Typography>
      ) : (
        <Box>
          {quotes.map(function(quote, index) {
            return (
              <QuoteCard
                key={quote.guid || index}
                quote={quote}
                showDecision={TABS[tab].status === 'Unconfirmed'}
                onDecision={onDecision}
              />
            )
          })}
          <TablePaginationInc
            page={currentPage}
            size={currentSize}
            total={total}
            onChange={handlePaginationChange}
          />
        </Box>
      )}

      <AlertDialog
        custom
        show={decisionLoader}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Recording quotation decision...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={decisionSuccess}
        size="sm"
        title="Quotation decision recorded."
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => setDecisionSuccess(false)}
        confirmBtnText="Ok"
        showCancel={false}
        confirmBtnCssClass
      />

      <AlertDialog
        show={!!decisionError}
        danger
        title="Could not record the quotation decision"
        onConfirm={() => setDecisionError(null)}
        confirmBtnText="Ok"
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        {readError(decisionError)}
      </AlertDialog>
    </Box>
  )
}

export default DisplayAmendedPolicies
